// Board selection, one entry per column: { [statusId]: { all, items: [{ startup_id, pipeline_id, investor_status_id }] } }.
// all = false: items are the picked cards. all = true: every card in the column except items (list view's select_all).
// Pure functions: unchanged columns keep their entry object, so memoized columns skip re-rendering.

const key = (id) => String(id);
const pick = (c) => ({ startup_id: c.startup_id, pipeline_id: c.pipeline_id, investor_status_id: c.investor_status_id });

const indexCache = new WeakMap();
const indexOf = (entry) => {
    let idx = indexCache.get(entry);
    if (!idx) {
        idx = new Set(entry.items.map((i) => key(i.startup_id)));
        indexCache.set(entry, idx);
    }
    return idx;
};

export const isSelected = (entry, startupId) => !!entry && entry.all !== indexOf(entry).has(key(startupId));

export const isCardSelected = (selection, card) => isSelected(selection?.[key(card.investor_status_id)], card.startup_id);

export const selectedInColumn = (entry, total) => {
    if (!entry) return 0;
    return entry.all ? Math.max(0, (total || 0) - entry.items.length) : entry.items.length;
};

// Header checkbox state: 'all' | 'some' | 'none'.
export const columnCheck = (entry, total) => {
    const n = selectedInColumn(entry, total);
    if (n === 0) return 'none';
    return total > 0 && n >= total ? 'all' : 'some';
};

export const selectedCount = (selection, columns) =>
    (columns || []).reduce((n, c) => n + selectedInColumn(selection?.[key(c._id)], c.count ?? (c.cards || []).length), 0);

export const hasSelection = (selection) => Object.keys(selection || {}).length > 0;

const put = (selection, colId, entry, total) => {
    const next = { ...selection };
    const empty = entry.all ? total != null && entry.items.length >= total : entry.items.length === 0;
    if (empty) delete next[colId];
    else next[colId] = entry;
    return next;
};

export const toggleCard = (selection, card, total) => {
    const colId = key(card.investor_status_id);
    const entry = selection[colId] || { all: false, items: [] };
    const listed = indexOf(entry).has(key(card.startup_id));
    const items = listed ? entry.items.filter((i) => key(i.startup_id) !== key(card.startup_id)) : [...entry.items, pick(card)];
    return put(selection, colId, { all: entry.all, items }, total);
};

// Shift+click range: every card ends up selected (all from one column).
export const selectCards = (selection, cards, total) => {
    if (!cards?.length) return selection;
    const colId = key(cards[0].investor_status_id);
    const entry = selection[colId] || { all: false, items: [] };
    const ids = new Set(cards.map((c) => key(c.startup_id)));
    const listed = indexOf(entry);
    const items = entry.all
        ? entry.items.filter((i) => !ids.has(key(i.startup_id)))
        : [...entry.items, ...cards.filter((c) => !listed.has(key(c.startup_id))).map(pick)];
    return put(selection, colId, { all: entry.all, items }, total);
};

export const selectColumn = (selection, colId) => ({ ...selection, [key(colId)]: { all: true, items: [] } });

export const clearColumn = (selection, colId) => {
    if (!selection[key(colId)]) return selection;
    const next = { ...selection };
    delete next[key(colId)];
    return next;
};

// For bulk actions: explicit picks, plus the columns selected as a whole (resolved on the server).
export const splitSelection = (selection) => {
    const picks = [];
    const scopes = [];
    Object.entries(selection || {}).forEach(([colId, entry]) => {
        if (entry.all) scopes.push({ investor_status_id: colId, exclude: new Set(entry.items.map((i) => key(i.startup_id))) });
        else picks.push(...entry.items);
    });
    return { picks, scopes };
};
